'use client';

import { useTranslations } from 'next-intl';
import { type ChangeEvent, FC, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { useUpdateNickname } from '@/lib/api/auth/hooks';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { Dialog, DialogContent, DialogTitle } from '@/lib/ui-kit/dialog';
import { Icon } from '@/lib/ui-kit/icons';
import { Button } from '@/lib/ui-kit-v2/button';
import { InputV2 } from '@/lib/ui-kit-v2/input';

interface DialogChangeUsernameProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

export const DialogChangeUsername: FC<DialogChangeUsernameProps> = ({ isOpen, setIsOpen }) => {
  const t = useTranslations('account.settings.changeUsername');
  const tCommon = useTranslations('common');
  const { user } = useAuthContext();
  const [value, setValue] = useState('');
  const { mutate, isPending } = useUpdateNickname();

  const schema = useMemo(
    () =>
      z
        .string()
        .trim()
        .min(3, t('validation.minLength'))
        .max(32, t('validation.maxLength'))
        .regex(/^[A-Za-z0-9][A-Za-z0-9_]{1,30}[A-Za-z0-9]$/, t('validation.latinOnly')),
    [t],
  );

  useEffect(() => {
    if (isOpen) {
      setValue(user?.name ?? '');
    }
  }, [isOpen, user?.name]);

  const parsed = schema.safeParse(value);
  const error = parsed.success
    ? undefined
    : (parsed.error.issues[0]?.message ?? t('validation.invalid'));

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    setValue(e.target.value);
  }

  function handleConfirm() {
    if (!parsed.success) return;
    mutate(parsed.data, {
      onSuccess: () => {
        toast.success(t('success'));
        setIsOpen(false);
      },
      onError: () => {
        toast.error(t('error'));
      },
    });
  }

  const isUnchanged = (user?.name ?? '') === value.trim();

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent
        className="flex w-[400px] max-w-[calc(100vw-32px)] flex-col gap-6 rounded-[16px] border
          border-v2-surface-tertiary bg-v2-surface-primary p-6"
        showCloseButton={false}
      >
        <DialogTitle className="flex items-center justify-between !pr-0">
          <span className="text-[24px] font-semibold leading-8 text-v2-text-primary">
            {t('title')}
          </span>
          <button
            type="button"
            onClick={() => setIsOpen(false)}
            className="flex size-10 shrink-0 cursor-pointer items-center justify-center text-v2-text-primary
              transition-opacity hover:opacity-80"
            aria-label={tCommon('close')}
          >
            <Icon name="Close" size={24} />
          </button>
        </DialogTitle>

        <InputV2
          label={t('label')}
          placeholder={t('placeholder')}
          value={value}
          onChange={handleChange}
          error={error}
        />

        <Button
          appearance="accent"
          size="M"
          className="w-full"
          onClick={handleConfirm}
          disabled={isPending || isUnchanged || !parsed.success}
        >
          {tCommon('confirm')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
